import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import ModalShell from '../../components/ModalShell';

const BulkDownloadModal = ({
  isOpen,
  onClose,
  isDrummondUser,
  isGenerating,
  jobProgress,
  jobStatus,
  message,
  onGenerate,
}) => {
  const [selected, setSelected] = useState(new Set());
  const [groups, setGroups] = useState([]); // [{label, domain, portfolios: [{code, name}]}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch portfolio data when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setError(null);
    fetchPortfolios();
  }, [isOpen]);

  const fetchPortfolios = async () => {
    setLoading(true);
    try {
      if (isDrummondUser) {
        const [portfoliosRes, brandsRes] = await Promise.all([
          api.get('/api/factsheets/portfolios'),
          api.get('/api/factsheets/portfolios/brands'),
        ]);

        const drummondPortfolios = (portfoliosRes.data.portfolios || [])
          .filter(p => p.has_portfolio_data !== false);
        const brands = brandsRes.data.brands || [];

        const brandResults = await Promise.all(
          brands.map(b =>
            api.get(`/api/factsheets/portfolios/brands/${encodeURIComponent(b.domain)}`)
              .then(res => ({
                label: b.company_name || b.domain,
                domain: b.domain,
                portfolios: (res.data.portfolios || [])
                  .filter(p => p.has_portfolio_data !== false)
                  .map(p => ({
                    code: p.portfolio_code,
                    name: p.name,
                  })),
              }))
              .catch(() => ({ label: b.company_name || b.domain, domain: b.domain, portfolios: [] }))
          )
        );

        const builtGroups = [
          {
            label: 'DRUMMOND PORTFOLIOS',
            domain: null,
            portfolios: drummondPortfolios.map(p => ({
              code: p.portfolio_code,
              name: p.name,
            })),
          },
          ...brandResults.filter(g => g.portfolios.length > 0),
        ];

        setGroups(builtGroups);
      } else {
        const res = await api.get('/api/factsheets/portfolios');
        const portfolios = (res.data.portfolios || [])
          .filter(p => p.has_portfolio_data !== false);
        setGroups([{
          label: 'PORTFOLIOS',
          domain: null,
          portfolios: portfolios.map(p => ({
            code: p.portfolio_code,
            name: p.name,
          })),
        }]);
      }
    } catch (err) {
      console.error('Error fetching portfolios for bulk download:', err);
      setError('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  const makeKey = (code, domain) => domain ? `${code}|||${domain}` : code;
  const parseKey = (key) => {
    const parts = key.split('|||');
    return { code: parts[0], domain: parts[1] || null };
  };

  const toggleItem = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectGroup = (group, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      group.portfolios.forEach(p => {
        const key = makeKey(p.code, group.domain);
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const isGroupAllSelected = (group) =>
    group.portfolios.length > 0 &&
    group.portfolios.every(p => selected.has(makeKey(p.code, group.domain)));

  const isGroupPartial = (group) => {
    const count = group.portfolios.filter(p => selected.has(makeKey(p.code, group.domain))).length;
    return count > 0 && count < group.portfolios.length;
  };

  const selectAll = () => {
    const all = new Set();
    groups.forEach(g => g.portfolios.forEach(p => all.add(makeKey(p.code, g.domain))));
    setSelected(all);
  };

  const selectNone = () => setSelected(new Set());

  const selectDrummondOnly = () => {
    const drummond = new Set();
    const dGroup = groups.find(g => g.domain === null);
    if (dGroup) dGroup.portfolios.forEach(p => drummond.add(makeKey(p.code, null)));
    setSelected(drummond);
  };

  const selectBrand = (domain) => {
    const brand = new Set();
    const bGroup = groups.find(g => g.domain === domain);
    if (bGroup) bGroup.portfolios.forEach(p => brand.add(makeKey(p.code, domain)));
    setSelected(brand);
  };

  const handleGenerate = () => {
    const items = Array.from(selected).map(key => parseKey(key));
    onGenerate(items);
  };

  const totalPortfolios = groups.reduce((sum, g) => sum + g.portfolios.length, 0);

  // Guard onClose during generation
  const guardedClose = isGenerating ? undefined : onClose;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={guardedClose}
      title="Bulk Download Fact Sheets"
      maxWidth="700px"
      initialWidth="700px"
      initialHeight="80vh"
    >
      {/* Body */}
      <div className="bulk-modal-body" style={{ padding: '16px 20px', flex: 1, overflowY: 'auto' }}>
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <p className="mt-2 text-muted mb-0" style={{ fontSize: '0.875rem' }}>Loading portfolios...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2" style={{ fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle me-1"></i>{error}
          </div>
        )}

        {!loading && !error && (
          <>
            {isDrummondUser && (
              <div className="bulk-cascade-bar">
                <button className="btn btn-sm btn-outline-primary" onClick={selectAll} disabled={isGenerating}>
                  Select All
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={selectNone} disabled={isGenerating}>
                  Clear
                </button>
                <button className="btn btn-sm btn-outline-primary" onClick={selectDrummondOnly} disabled={isGenerating}>
                  Drummond Only
                </button>
                {groups.filter(g => g.domain !== null).map(g => (
                  <button
                    key={g.domain}
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => selectBrand(g.domain)}
                    disabled={isGenerating}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            )}

            {groups.map((group) => (
              <div key={group.domain || '_drummond'} style={{ marginBottom: '0.75rem' }}>
                <label className="bulk-group-header">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isGroupAllSelected(group)}
                    ref={input => {
                      if (input) input.indeterminate = isGroupPartial(group);
                    }}
                    onChange={(e) => selectGroup(group, e.target.checked)}
                    disabled={isGenerating}
                  />
                  {group.label} ({group.portfolios.length})
                </label>
                {group.portfolios.map(p => {
                  const key = makeKey(p.code, group.domain);
                  return (
                    <label key={key} className="bulk-portfolio-item">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selected.has(key)}
                        onChange={() => toggleItem(key)}
                        disabled={isGenerating}
                      />
                      {p.name}
                    </label>
                  );
                })}
              </div>
            ))}
          </>
        )}

        {isGenerating && (
          <div style={{ marginTop: '1rem' }}>
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">
                {jobStatus === 'pending' && 'Starting...'}
                {jobStatus === 'running' && 'Generating fact sheets...'}
                {jobStatus === 'completed' && 'Preparing download...'}
              </small>
              <small className="text-muted">{jobProgress}%</small>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${jobProgress}%`, background: '#072040' }}
                aria-valuenow={jobProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>
        )}

        {message && (
          <div
            className={`alert alert-${message.type === 'error' ? 'danger' : message.type === 'warning' ? 'warning' : message.type === 'info' ? 'info' : 'success'} py-2 mt-3`}
            style={{ fontSize: '0.85rem' }}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bulk-modal-footer" style={{
        padding: '12px 20px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
          {selected.size} of {totalPortfolios} selected
        </span>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={isGenerating || selected.size === 0}
            style={{ background: '#072040', borderColor: '#072040' }}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Generating... {jobProgress}%
              </>
            ) : (
              <>
                <i className="bi bi-download me-2"></i>
                Download ({selected.size})
              </>
            )}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default BulkDownloadModal;
